import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { EmptyState, useSortable } from "@/components/common";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Tag } from "lucide-react";
import { priceListService } from "@/services/priceListService";
import { PriceListSidebar, PriceForm, PriceTable } from "./PriceListParts";
import { PriceAuthorityPanel } from "./PriceAuthorityPanel";

const PL_ACCESSORS = {
  artikel: (p) => p.name || p.label || "",
  netto: (p) => Number(p.price_net) || 0,
  mwst: (p) => Number(p.tax_rate) || 0,
  brutto: (p) => Number(p.price_gross) || 0,
  einstand: (p) => Number(p.cost_price) || 0,
  ertrag_abs: (p) => (Number(p.price_net) || 0) - (Number(p.cost_price) || 0),
  ertrag_pct: (p) => { const n = Number(p.price_net) || 0; return n > 0 ? (n - (Number(p.cost_price) || 0)) / n : 0; },
  gueltig: (p) => p.valid_from || "",
  status: (p) => (p.valid_now ? 1 : 0),
};

export default function PriceListsTab({ canEdit, canCost }) {
  const { t } = useI18n();
  const [priceLists, setPriceLists] = useState([]);
  const [variants, setVariants] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [settings, setSettings] = useState(null);
  const [plOpen, setPlOpen] = useState(false);
  const [plName, setPlName] = useState("");
  const [activePl, setActivePl] = useState(null);
  const [pf, setPf] = useState({ kind: "variant", ref: "", price_net: "", tax_rate: 19, valid_from: "", valid_to: "" });
  const [editRow, setEditRow] = useState(null);
  const [q, setQ] = useState("");
  const visiblePrices = (activePl?.prices || []).filter((p) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return `${p.name || ""} ${p.label || ""}`.toLowerCase().includes(s);
  });
  const { sorted: sortedPrices, sortKey: plSortKey, sortDir: plSortDir, toggle: plToggle } = useSortable(visiblePrices, PL_ACCESSORS);

  const load = useCallback(() => {
    priceListService.list().then((d) => setPriceLists([...d].sort((a, b) => (a.name || "").localeCompare(b.name || "", "de")))).catch(() => {});
    api.get("/variants").then((r) => setVariants([...r.data].sort((a, b) => (a.product_name || "").localeCompare(b.product_name || "", "de") || (a.sku || "").localeCompare(b.sku || "", "de")))).catch(() => {});
    api.get("/bundles").then((r) => setBundles([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "de")))).catch(() => {});
  }, []);
  useEffect(() => { load(); api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, [load]);

  const taxRates = settings?.tax_rates || [0, 7, 19];
  const r2 = (n) => Math.round(n * 100) / 100;
  const plGross = (net, tax) => (net === "" || isNaN(Number(net)) ? 0 : r2(Number(net) * (1 + (Number(tax) || 0) / 100)));

  const createPl = async () => { try { await priceListService.create({ name: plName }); toast.success(t("pl.created")); setPlOpen(false); setPlName(""); load(); } catch (e) { toast.error(apiError(e)); } };
  const openPl = async (id) => { const d = await priceListService.get(id); setActivePl(d); };
  const renamePl = async (pl) => { const name = window.prompt(t("pl.renamePrompt"), pl.name); if (!name || name.trim() === "" || name === pl.name) return; try { await priceListService.update(pl.id, { name: name.trim() }); toast.success(t("pl.renamed")); load(); if (activePl?.id === pl.id) openPl(pl.id); } catch (e) { toast.error(apiError(e)); } };
  const archivePl = async (pl) => { if (!window.confirm(t("pl.archiveConfirm", { name: pl.name }))) return; try { await priceListService.update(pl.id, { archived: true }); toast.success(t("common.archived")); if (activePl?.id === pl.id) setActivePl(null); load(); } catch (e) { toast.error(apiError(e)); } };
  const deletePl = async (pl) => { if (!window.confirm(t("pl.deleteConfirm", { name: pl.name }))) return; try { await priceListService.remove(pl.id); toast.success(t("prod.deleted")); if (activePl?.id === pl.id) setActivePl(null); load(); } catch (e) { toast.error(apiError(e)); } };
  const addPrice = async () => {
    try {
      const body = { price_net: Number(pf.price_net) || 0, price_gross: plGross(pf.price_net, pf.tax_rate), tax_rate: Number(pf.tax_rate) || 0,
        valid_from: pf.valid_from || null, valid_to: pf.valid_to || null, [pf.kind === "bundle" ? "bundle_id" : "variant_id"]: pf.ref };
      await priceListService.addPrice(activePl.id, body);
      toast.success(t("pl.priceSet")); setPf({ kind: "variant", ref: "", price_net: "", tax_rate: 19, valid_from: "", valid_to: "" }); openPl(activePl.id); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const delPrice = async (pid) => { try { await priceListService.removePrice(activePl.id, pid); toast.success(t("pl.priceDeleted")); openPl(activePl.id); load(); } catch (e) { toast.error(apiError(e)); } };
  const marginOf = (net, cost) => { const n = Number(net) || 0, c = Number(cost) || 0; const abs = r2(n - c); const pct = n > 0 ? r2((abs / n) * 100) : 0; return { abs, pct }; };
  const startEdit = (p) => setEditRow({ id: p.id, variant_id: p.variant_id || null, bundle_id: p.bundle_id || null, price_net: p.price_net, tax_rate: p.tax_rate ?? 19, valid_from: p.valid_from || "", valid_to: p.valid_to || "" });
  const saveEditPrice = async () => {
    try {
      const e = editRow;
      const body = { price_net: Number(e.price_net) || 0, price_gross: plGross(e.price_net, e.tax_rate), tax_rate: Number(e.tax_rate) || 0,
        valid_from: e.valid_from || null, valid_to: e.valid_to || null, [e.bundle_id ? "bundle_id" : "variant_id"]: e.bundle_id || e.variant_id };
      await priceListService.updatePrice(activePl.id, e.id, body);
      toast.success(t("pl.priceUpdated")); setEditRow(null); openPl(activePl.id); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <>
      {canEdit && <div className="flex justify-end mb-3"><Button data-testid="new-pricelist-btn" onClick={() => setPlOpen(true)}><Plus className="h-4 w-4 mr-1.5" /> {t("pl.new")}</Button></div>}
      {priceLists.length === 0 ? <EmptyState testId="pricelists-empty" icon={Tag} title={t("pl.emptyTitle")} hint={t("pl.emptyHint")} /> : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <PriceListSidebar priceLists={priceLists} activePl={activePl} canEdit={canEdit} onOpen={openPl} onRename={renamePl} onArchive={archivePl} onDelete={deletePl} />
          <div className="lg:col-span-2">
            {activePl ? (
              <div className="rounded-md border border-border bg-card p-4 shadow-sm">
                <div className="font-heading font-medium mb-3">{activePl.name}</div>
                <Tabs defaultValue="prices">
                  <TabsList>
                    <TabsTrigger value="prices" data-testid="pl-tab-prices">{t("prod.tabPrices")}</TabsTrigger>
                    <TabsTrigger value="authority" data-testid="pl-tab-authority">{t("prod.tabTax")}</TabsTrigger>
                  </TabsList>
                  <TabsContent value="prices" className="mt-3">
                    {canEdit && <PriceForm pf={pf} setPf={setPf} taxRates={taxRates} bundles={bundles} variants={variants} plGross={plGross} onAdd={addPrice} />}
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("pl.searchItems")}
                        className="h-9 w-full sm:w-64" data-testid="pl-search" />
                      <span className="text-xs text-muted-foreground" data-testid="pl-count">
                        {t("pl.countFiltered", { shown: visiblePrices.length, total: (activePl.prices || []).length })}
                      </span>
                    </div>
                    <div className="overflow-x-auto">
                      <PriceTable sortedPrices={sortedPrices} plSortKey={plSortKey} plSortDir={plSortDir} plToggle={plToggle} editRow={editRow} setEditRow={setEditRow} canEdit={canEdit} canCost={canCost} taxRates={taxRates} plGross={plGross} marginOf={marginOf} onStartEdit={startEdit} onSaveEdit={saveEditPrice} onDelPrice={delPrice} hasPrices={visiblePrices.length > 0} />
                    </div>
                  </TabsContent>
                  <TabsContent value="authority" className="mt-3">
                    <PriceAuthorityPanel priceList={activePl} canEdit={canEdit} onSaved={() => openPl(activePl.id)} />
                  </TabsContent>
                </Tabs>
              </div>
            ) : <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">{t("pl.selectLeft")}</div>}
          </div>
        </div>
      )}

      <Dialog open={plOpen} onOpenChange={setPlOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("pl.new")}</DialogTitle></DialogHeader>
          <div><Label>{t("common.name")}</Label><Input value={plName} onChange={(e) => setPlName(e.target.value)} className="mt-1.5" data-testid="pl-name" /></div>
          <DialogFooter><Button onClick={createPl} disabled={!plName} data-testid="save-pl-btn">{t("pl.create")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
